import React from 'react';
import { Flex, Table } from 'antd';
import { Link } from 'react-router-dom';
import { DATE_AND_TIME, TimeZoneFormat, FixtureTypes } from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { formatQuantity } from '@shared/utils/string';
import { timeZoneOffsetLabel } from '@shared/utils/timezones';
import { GetVoyagesResponse } from '@api/features/voyages/getVoyages';
import { sortByFuelGrade } from '@client/screens/fleet/VoyageDetailsScreen/components/ItineraryTab/utils/helpers';
import { Links } from '@client/utils/links';
import Details from './Details';
import EstimatedActualTag from './EstimatedActualTag';

type Props = {
	selectedPreviousContractId: number | null | undefined;
	voyages: GetVoyagesResponse['rows'];
};

const LinkedContractData = ({
	selectedPreviousContractId,
	voyages = [],
}: Props) => {
	if (!selectedPreviousContractId) {
		return null;
	}

	const relevantVoyage = voyages.find((v) => v.id === selectedPreviousContractId);

	if (!relevantVoyage) {
		return null;
	}

	let isDateEstimated = relevantVoyage.isCompletionDateForLinkingEstimated;
	let isPortEstimated = relevantVoyage.isCompletionPortForLinkingEstimated;
	let dateValue: string | React.ReactElement = 'N/A';
	let portValue = 'N/A';
	let bunkerValues = relevantVoyage.completionBunkersForLinking ?? [];

	if (relevantVoyage.completionDateForLinking){
		dateValue = <>
			{relevantVoyage.completionDateForLinking?.format(DATE_AND_TIME)}
			{timeZoneOffsetLabel(
				relevantVoyage.completionDateForLinking, 
				relevantVoyage.completionPortForLinking?.timezone, 
				TimeZoneFormat.LocalTime
			)}
		</>;
	}

	if (relevantVoyage.completionPortForLinking) {
		portValue = relevantVoyage.completionPortForLinking.name;
	}
		
	let portKey = 'completionPort';
	let portLabel = 'Completion Port';
	let dateKey = 'completionDate';
	let dateLabel = 'Completion Date';

	if (relevantVoyage.fixtureType === FixtureTypes.TC_IN) {
		portKey = 'deliveryPort';
		portLabel = 'Delivery Port';

		dateKey = 'deliveryDate';
		dateLabel = 'Delivery Date';	

		isDateEstimated = relevantVoyage.isCompletionDateForLinkingEstimated;
		isPortEstimated = relevantVoyage.isCompletionPortForLinkingEstimated;

		const date = relevantVoyage.completionDateForLinking;

		bunkerValues = relevantVoyage.tcInBunkersForLinking;
		portValue = relevantVoyage?.deliveryPort?.name ?? 'N/A';
		dateValue = <>
			{date?.format(DATE_AND_TIME)}
			{timeZoneOffsetLabel(
				date, 
				relevantVoyage.deliveryPort?.timezone, 
				TimeZoneFormat.LocalTime
			)}
		</>;
	}

	return (
		<Flex vertical style={{ minWidth: '100%' }} gap={10}>
			<Details
				style={{ width: '100%' }}
				title={null}
				items={[{
					key: 'selectedContract',
					label: 'Selected contract',
					value: (
						<Link to={Links.Voyage.get(relevantVoyage.id)}>
							{relevantVoyage.identifier}
						</Link>
					)
				},
				{
					key: portKey,
					label: portLabel,
					value: (
						<Flex
							gap={10} align="center">
							<EstimatedActualTag
								estimated={isPortEstimated}
								data-testid="completion-port-tag"
							/>
							<p
								data-testid="completion-port"
								style={{ padding: 0, margin: 0 }}
							>
								{portValue}
							</p>
						</Flex>
					)
				}, 
				{
					key: dateKey,
					label: dateLabel,
					value: (
						<Flex 
							gap={10}
							align="center"
						>
							<EstimatedActualTag
								estimated={isDateEstimated}
								data-testid="completion-date-tag"
							/>
							<p
								data-testid="completion-date"
								style={{ padding: 0, margin: 0 }}
							>
								{dateValue}
							</p>
						</Flex>
					)
				}]}
			/>
			<Table
				data-testid="completion-bunkers"
				dataSource={sortByFuelGrade(bunkerValues)}
				columns={[{
					dataIndex: 'fuelGrade',
					title: 'Fuel grade'
				}, {
					dataIndex: 'quantity',
					title: 'Quantity',
					render: (v) => v ? formatQuantity(v, 'MT') : '-'
				}, {
					dataIndex: 'pricePerTon',
					title: 'Price / MT',
					render: (v) => formatCurrency(v ?? 0, relevantVoyage.currency)
				}]}
				pagination={false}
				size='small'
			/>
		</Flex>
	);
};

export default LinkedContractData;
